import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FilterSearchInput } from "@/components/ui/filter-search-input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "@/components/ui/sonner";
import {
  linkLicenseMutation,
  listLicenseLinkCandidatesOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type { LicenseLinkCandidateSchema } from "@/hera-api/client/types.gen";
import { useDebounce } from "@/hooks/use-debounce";
import { notifyLicenseDetailsChanged } from "@/lib/license-details-change-notification";
import {
  formatDate,
  formatDateNoTimezone,
  getQueryErrorMessage,
  getRegistrationStatusDisplay,
} from "@/lib/utils";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useState } from "react";

interface LinkLicenseDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  licenseId: string;
}

export function LinkLicenseDialog({
  isOpen,
  onOpenChange,
  licenseId,
}: LinkLicenseDialogProps) {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string>("");
  const [apnSource, setApnSource] = useState<"current" | "candidate" | "">("");
  const [copyAdditionalQuestions, setCopyAdditionalQuestions] = useState(false);
  const [licenseIdQuery, setLicenseIdQuery] = useState("");
  const [addressQuery, setAddressQuery] = useState("");
  const [startDateChoice, setStartDateChoice] = useState("");
  const debouncedLicenseIdQuery = useDebounce(licenseIdQuery, 300);
  const debouncedAddressQuery = useDebounce(addressQuery, 300);
  const activeLicenseIdQuery =
    licenseIdQuery.trim() === "" ? "" : debouncedLicenseIdQuery.trim();
  const activeAddressQuery =
    addressQuery.trim() === "" ? "" : debouncedAddressQuery.trim();
  const hasSearch = activeLicenseIdQuery !== "" || activeAddressQuery !== "";

  const candidatesQuery = useQuery({
    ...listLicenseLinkCandidatesOptions({
      path: { license_id: licenseId },
      query: {
        display_id: activeLicenseIdQuery || undefined,
        property_address: activeAddressQuery || undefined,
      },
    }),
    enabled: isOpen && !!licenseId,
    placeholderData: keepPreviousData,
  });

  const linkMutation = useMutation({
    ...linkLicenseMutation(),
  });

  const candidates = candidatesQuery.data?.candidates ?? [];
  const selectable = candidates.filter((candidate) => candidate.selectable);
  const selectedCandidate = candidates.find(
    (candidate) => candidate.id === selectedId,
  );
  const currentEffectiveDate = candidatesQuery.data?.current_effective_date;
  const startDatesDiffer =
    !!selectedCandidate &&
    !!currentEffectiveDate &&
    selectedCandidate.effective_date !== currentEffectiveDate;
  const apnsDiffer =
    !!selectedCandidate &&
    selectedCandidate.current_property_apn !== selectedCandidate.property_apn;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedId("");
      setApnSource("");
      setCopyAdditionalQuestions(false);
      setLicenseIdQuery("");
      setAddressQuery("");
      setStartDateChoice("");
    }
    onOpenChange(open);
  };

  const selectCandidate = (candidateId: string) => {
    setSelectedId(candidateId);
    setApnSource("");
    setCopyAdditionalQuestions(false);
    setStartDateChoice("");
  };

  const handleLink = async () => {
    if (!selectedId) return;
    try {
      const result = await linkMutation.mutateAsync({
        path: { license_id: licenseId },
        body: {
          target_license_id: selectedId,
          apn_to_keep:
            apnSource === "candidate"
              ? (selectedCandidate?.property_apn ?? null)
              : (selectedCandidate?.current_property_apn ?? null),
          copy_additional_questions: copyAdditionalQuestions,
          effective_date_to_keep: startDatesDiffer ? startDateChoice : null,
        },
      });
      const affectedIds = [
        licenseId,
        result.newest_license_id,
        ...result.cancelled_license_ids,
      ];
      await Promise.all(
        [...new Set(affectedIds)].map((id) =>
          notifyLicenseDetailsChanged(queryClient, id),
        ),
      );
      toast({
        title: "Licenses linked",
        description: `${result.newest_display_id} now holds outstanding fees. Older licenses were cancelled.`,
        variant: "secondary",
      });
      handleOpenChange(false);
    } catch (error) {
      toast({
        title: "Unable to link licenses",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent elevated className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Link License</DialogTitle>
          <DialogDescription>
            Proposed matches share this property. Search by license ID or
            property address to look through every license in this jurisdiction
            and link it with{" "}
            {candidatesQuery.data?.current_display_id ?? "this license"}. The
            newest license by application date will keep outstanding fees. All
            others will be cancelled with a reason of “Linked to licence …”.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="overflow-hidden rounded-md border">
            <div className="border-b px-3 py-2 text-sm font-medium text-muted-foreground">
              License ID
            </div>
            <FilterSearchInput
              placeholder="Search license ID..."
              value={licenseIdQuery}
              onValueChange={setLicenseIdQuery}
            />
            <div className="border-b border-t px-3 py-2 text-sm font-medium text-muted-foreground">
              Property Address
            </div>
            <FilterSearchInput
              placeholder="Search property address..."
              value={addressQuery}
              onValueChange={setAddressQuery}
            />
          </div>

          {candidatesQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">
              {hasSearch
                ? "Searching licenses in this jurisdiction…"
                : "Loading licenses on this property…"}
            </p>
          ) : candidatesQuery.isError ? (
            <p className="text-sm text-destructive">
              {getQueryErrorMessage(candidatesQuery.error)}
            </p>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {hasSearch
                ? "No licenses in this jurisdiction match that license ID or address."
                : "There are no other licenses on this property."}
            </p>
          ) : (
            <>
              <RadioGroup
                value={selectedId}
                onValueChange={selectCandidate}
                className="max-h-80 overflow-y-auto pr-1"
              >
                {candidates.map((candidate) => (
                  <CandidateOption key={candidate.id} candidate={candidate} />
                ))}
              </RadioGroup>
              {hasSearch ? (
                <p className="text-xs text-muted-foreground">
                  {candidatesQuery.data?.truncated
                    ? `Showing the first ${candidates.length} licenses in this jurisdiction. Refine the search to narrow the results.`
                    : `${candidates.length} ${
                        candidates.length === 1 ? "license" : "licenses"
                      } in this jurisdiction`}
                </p>
              ) : null}
            </>
          )}
        </div>

        {selectedCandidate ? (
          <div className="space-y-3 rounded-md border p-3">
            {startDatesDiffer && currentEffectiveDate ? (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Choose the start date all linked licenses should use
                </p>
                <RadioGroup
                  value={startDateChoice}
                  onValueChange={setStartDateChoice}
                >
                  <label
                    htmlFor="link-start-current"
                    className="flex items-center gap-2 text-sm"
                  >
                    <RadioGroupItem
                      id="link-start-current"
                      value={currentEffectiveDate}
                    />
                    {formatDateNoTimezone(currentEffectiveDate)} (
                    {candidatesQuery.data?.current_display_id})
                  </label>
                  <label
                    htmlFor="link-start-candidate"
                    className="flex items-center gap-2 text-sm"
                  >
                    <RadioGroupItem
                      id="link-start-candidate"
                      value={selectedCandidate.effective_date}
                    />
                    {formatDateNoTimezone(selectedCandidate.effective_date)} (
                    {selectedCandidate.display_id})
                  </label>
                </RadioGroup>
              </div>
            ) : null}

            {apnsDiffer ? (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Choose the APN both licenses should use
                </p>
                <RadioGroup
                  value={apnSource}
                  onValueChange={(value) =>
                    setApnSource(value as "current" | "candidate")
                  }
                >
                  <label
                    htmlFor="link-apn-current"
                    className="flex items-center gap-2 text-sm"
                  >
                    <RadioGroupItem id="link-apn-current" value="current" />
                    {selectedCandidate.current_property_apn || "(blank APN)"}
                  </label>
                  <label
                    htmlFor="link-apn-candidate"
                    className="flex items-center gap-2 text-sm"
                  >
                    <RadioGroupItem id="link-apn-candidate" value="candidate" />
                    {selectedCandidate.property_apn || "(blank APN)"}
                  </label>
                </RadioGroup>
                <p className="text-xs text-muted-foreground">
                  Both licenses will point to the same property record.
                </p>
              </div>
            ) : null}

            {selectedCandidate.address_will_change ? (
              <p className="text-sm">
                The street addresses differ. All linked licenses will use{" "}
                <strong>{selectedCandidate.canonical_address}</strong>, from the
                license with the earliest effective start date.
              </p>
            ) : null}

            {selectedCandidate.can_copy_additional_questions ? (
              <label
                htmlFor="copy-link-additional-questions"
                className="flex items-start gap-2 text-sm"
              >
                <Checkbox
                  id="copy-link-additional-questions"
                  checked={copyAdditionalQuestions}
                  onCheckedChange={(checked) =>
                    setCopyAdditionalQuestions(checked === true)
                  }
                  className="mt-0.5"
                />
                <span>
                  {selectedCandidate.newest_display_id} has no additional
                  question answers. Copy them from{" "}
                  {selectedCandidate.question_source_display_id}?
                </span>
              </label>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleLink()}
            disabled={
              !selectedId ||
              (apnsDiffer && !apnSource) ||
              (startDatesDiffer && !startDateChoice) ||
              linkMutation.isPending ||
              selectable.length === 0 ||
              !selectable.some((candidate) => candidate.id === selectedId)
            }
          >
            {linkMutation.isPending ? "Linking…" : "Link License"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CandidateOption({
  candidate,
}: {
  candidate: LicenseLinkCandidateSchema;
}) {
  const status = getRegistrationStatusDisplay(candidate.status);
  return (
    <label
      htmlFor={`link-license-${candidate.id}`}
      className="flex items-start gap-3 rounded-md border p-3 has-[:disabled]:opacity-60"
    >
      <RadioGroupItem
        id={`link-license-${candidate.id}`}
        value={candidate.id}
        disabled={!candidate.selectable}
        className="mt-0.5"
      />
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-medium">{candidate.display_id}</p>
        <p className="text-xs text-muted-foreground">
          {candidate.license_type_name}
          {candidate.registrant_label ? ` · ${candidate.registrant_label}` : ""}
        </p>
        <p className="text-xs text-muted-foreground">
          {candidate.property_address} · APN{" "}
          {candidate.property_apn || "(blank)"}
        </p>
        <p className="text-xs text-muted-foreground">
          {status.label} · Start{" "}
          {formatDateNoTimezone(candidate.effective_date)} · Applied{" "}
          {formatDate(candidate.created_at)}
          {candidate.payment_group_parent_display_id
            ? ` · Parent ${candidate.payment_group_parent_display_id}`
            : ""}
        </p>
        {candidate.disabled_reason ? (
          <p className="text-xs text-destructive">
            {candidate.disabled_reason}
          </p>
        ) : null}
      </div>
    </label>
  );
}
